import React from 'react';
import { useTranslations } from 'next-intl';
import { first, last } from 'lodash';
import { Button, ButtonGroup } from '@/components/common/Button';
import { DataGrid } from '@/components/common/DataGrid';
import { Modal } from '@/components/common/Modal';
import { TripRoute } from '@/components/pages/SituationalAwareness/MapView/MapNavigation/MapNavigation.types';
import { Formatter } from '@/core/utils/Formatter';
import styles from './ConfirmationRouteModal.module.scss';

const formatter = new Formatter({
  dateOptions: {
    dateStyle: undefined,
    timeStyle: 'short',
  },
});

const getStationName = (route: TripRoute) => first(route.stops)?.location as string;
const getRouteDriveTime = (route: TripRoute) => {
  const startTime = first(route.stops)?.eta as string;
  const stopTime = last(route.stops)?.eta as string;
  const timeDiffMs = new Date(stopTime).valueOf() - new Date(startTime).valueOf();
  const timeDiff = Formatter.secondsToString(timeDiffMs / 1000);

  return { startTime, stopTime, timeDiff };
};

export const ConfirmationRouteModal: React.FC<{ routes: TripRoute[]; onClose: () => void; onConfirm: () => void }> = ({
  routes,
  onConfirm,
  onClose,
}) => {
  const t = useTranslations();
  const [route, ...supportiveRoutes] = routes;
  const { startTime, stopTime, timeDiff } = getRouteDriveTime(route);
  const mainStationName = getStationName(route);
  const supportiveStationNames = supportiveRoutes.map((item) => ({
    name: getStationName(item),
    timeDiff: getRouteDriveTime(item).timeDiff,
  }));

  return (
    <Modal isOpen onClose={onClose} isCentered closeOnOverlayClick={false} size="xl">
      <Modal.Overlay />
      <Modal.Content className={styles.container} overflow="auto" minWidth={700}>
        <Modal.Body className={styles.body}>
          <div className={styles.header}>
            <p style={{ fontSize: 32, fontWeight: 600 }}>Optimal Path And Crew Found</p>
            <div>Do you confirm sending the selected crew via the fastest route?</div>
          </div>
          <DataGrid
            labelClassName={styles.gridLabel}
            valueClassName={styles.gridValue}
            data={[
              { label: t('start-time'), value: formatter.date.format(new Date(startTime)) },
              { label: t('arrival-time'), value: formatter.date.format(new Date(stopTime)) + ` (${timeDiff})` },
              { label: t('first-responder'), value: mainStationName },
              {
                label: t('supportive-stations'),
                value: supportiveStationNames.map((item) => (
                  <div key={item.name}>
                    <span>{item.name}</span>
                    <small>({item.timeDiff})</small>
                  </div>
                )),
              },
            ]}
          />
        </Modal.Body>
        <Modal.Footer padding="0">
          <ButtonGroup spacing="0" size="lg" style={{ width: '100%' }}>
            <Button label={t('cancel')} size="lg" rounded="l2" flex="0 1 50%" onClick={onClose} />
            <Button label={t('confirm')} size="lg" rounded="l2" type="submit" flex="0 1 50%" onClick={onConfirm} />
          </ButtonGroup>
        </Modal.Footer>
      </Modal.Content>
    </Modal>
  );
};
